import { Autocomplete, Chip, CircularProgress, FormControl, Grid, TextField } from '@mui/material';
import React, { Fragment, useCallback, useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import { useSelector } from 'react-redux';
import FieldNames from '../../defaults/FieldNames.json';
import ErrorService from '../../services/ErrorService';
import UserHelperService from '../../services/UserHelperService';
import DetailsPopover from '../details/customPanels/DetailsPopover';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const isMatching = (field, value) =>
    Boolean(field?.toLowerCase().includes(value));

const getRowValue = (row, field) => row?.[field] ?? '';

const uniqueList = (items, key) => {
    const seen = new Set();
    return items.reduce((acc, item) => {
        const val = key ? item[key] : item;
        if (val && !seen.has(val)) { seen.add(val); acc.push(val); }
        return acc;
    }, []);
};

// ─── Sub-component: reusable autocomplete row ────────────────────────────────

function FundingAutocomplete({
    id,
    label,
    value,
    options,
    loading = false,
    disabled = false,
    requiredFields = [],
    fieldKey,
    isTreatment,
    isActual,
    isPoint,
    agencyValue,
    onChange,
    onOpen,
    onClose,
    renderTags,
}) {
    const isRequired = requiredFields.includes(fieldKey);
    return (
        <FormControl variant="standard" sx={{ margin: '15px', minWidth: 120, width: '90%' }}>
            <Grid container direction="row" size={12} style={{ alignItems: 'center' }}>
                <Grid size={6}>
                    <span>
                        <label>{label}</label>
                        {isRequired && <span className="required">*</span>}
                        <DetailsPopover
                            mode="edit"
                            field={fieldKey}
                            isTreatment={isTreatment}
                            categoryValue={null}
                            isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no}
                            isActuals={isActual}
                            agencyValue={agencyValue}
                            isBulkApproval={false}
                        />
                    </span>
                </Grid>
                <Grid size={6}>
                    <Autocomplete
                        id={id}
                        value={value ?? ''}
                        options={options}
                        loading={loading}
                        disabled={disabled}
                        onChange={onChange}
                        onOpen={onOpen}
                        onClose={onClose}
                        isOptionEqualToValue={(option, val) => option === val}
                        getOptionLabel={(option) => option ?? ''}
                        renderTags={renderTags}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {loading && <CircularProgress color="inherit" size={20} />}
                                                {params?.InputProps?.endAdornment}
                                            </React.Fragment>
                                        ),
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>
            </Grid>
        </FormControl>
    );
}

// ─── Custom hook: manages open / options / loading for an autocomplete ────────

function useAutocompleteField(fetchFn, deps = []) {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const loading = open && options.length === 0;

    useEffect(() => {
        if (!loading) return;
        let active = true;
        fetchFn().then((result) => {
            if (active) setOptions(result ?? ['']);
        });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, ...deps]);

    useEffect(() => {
        if (!open) setOptions([]);
    }, [open]);

    return { open, setOpen, options, loading };
}

// ─── Main component ───────────────────────────────────────────────────────────
//
// Props:
//   selectedRow      – row data used to initialise field values
//   rows             – sibling funding rows (for BIL detection)
//   requiredFields   – array of field keys that are mandatory
//   isTreatment      – boolean
//   isActual         – boolean
//   isPoint          – boolean
//   isInteragencyRow – boolean, locks fields to OWF values
//   onChange         – (values) => void — called whenever any selection changes
//
// Imperative handle (via ref):
//   getValues()      – returns a snapshot of all current selections
//   isValid()        – returns whether all required fields are filled
//

interface FundingAssignmentProps {
    selectedRow?: any;
    rows?: any[];
    requiredFields?: string[];
    isTreatment?: boolean;
    isActual?: boolean;
    isPoint?: boolean;
    isInteragencyRow?: boolean;
    onChange?: (values: any) => void;
    onValidityChange?: (valid: boolean) => void;
}

const FundingAssignment = forwardRef<any, FundingAssignmentProps>(function FundingAssignment({
    selectedRow,
    rows,
    requiredFields = [],
    isTreatment = false,
    isActual = false,
    isPoint = false,
    isInteragencyRow = false,
    onChange,
    onValidityChange,
}, ref) {
    // ── Read user data from Redux (no prop drilling) ──────────────────────────
    const userInfo = useSelector((state) => state.globalContext.userInfo);
    const userRoleInfo = useSelector((state) => state.globalContext.userRoleInfo);

    // ── Local state ───────────────────────────────────────────────────────────
    const [department, setDepartment] = useState('');
    const [agency, setAgency] = useState('');
    const [region, setRegion] = useState('');
    const [unit, setUnit] = useState('');
    const [subUnit, setSubUnit] = useState('');
    const [tribe, setTribe] = useState('');
    const [fundingUnitType, setFundingUnitType] = useState('');
    const [costCenterCode, setCostCenterCode] = useState('');
    const [fundingUnitID, setFundingUnitID] = useState('');
    const [isBil, setIsBil] = useState(false);

    const [departmentOptions] = useState([FieldNames.defaults.department]);
    const [fundingUnitTypeOptions, setFundingUnitTypeOptions] = useState([]);

    const logError = useCallback(
        (error) => ErrorService.addErrorLogEntry(userInfo?.[FieldNames.userFields.nifcUsername], error),
        [userInfo]
    );

    // ── Build a snapshot of all current values ────────────────────────────────
    const buildValues = useCallback(() => ({
        [FieldNames.fundingFields.fundingDepartment]: department || null,
        [FieldNames.fundingFields.fundingAgency]: agency || null,
        [FieldNames.fundingFields.fundingRegion]: region || null,
        [FieldNames.fundingFields.fundingUnit]: unit || null,
        [FieldNames.fundingFields.fundingSubUnit]: subUnit || null,
        [FieldNames.fundingFields.fundingTribe]: tribe || null,
        [FieldNames.fundingFields.fundingUnitType]: fundingUnitType || null,
        [FieldNames.fundingFields.costCenter]: costCenterCode || null,
        [FieldNames.fundingFields.fundingUnitID]: fundingUnitID || null,
        isBil,
    }), [department, agency, region, unit, subUnit, tribe, fundingUnitType, costCenterCode, fundingUnitID, isBil]);

    // ── Notify parent whenever values change ──────────────────────────────────
    useEffect(() => {
        onChange?.(buildValues());
    }, [buildValues, onChange]);

    // ── Validation helper ─────────────────────────────────────────────────────
    const checkValidity = useCallback(() => {
        const checks = [
            [FieldNames.featureFields.fundingDepartment, department],
            [FieldNames.featureFields.fundingAgency, agency],
            [FieldNames.featureFields.fundingRegion, region],
            [FieldNames.featureFields.fundingUnit, unit],
            [FieldNames.featureFields.fundingSubUnit, subUnit],
            [FieldNames.featureFields.fundingTribe, tribe],
            [FieldNames.featureFields.fundingUnitType, fundingUnitType],
        ];
        return checks.every(([field, value]) => !requiredFields.includes(field) || Boolean(value));
    }, [department, agency, region, unit, subUnit, tribe, fundingUnitType, requiredFields]);

    // ── Notify parent when validity changes ───────────────────────────────────
    useEffect(() => {
        onValidityChange?.(checkValidity());
    }, [checkValidity, onValidityChange]);

    // ── Imperative handle ─────────────────────────────────────────────────────
    useImperativeHandle(ref, () => ({
        getValues: buildValues,
        isValid: checkValidity,
    }), [buildValues, checkValidity]);

    // ── Fetch unit entry → update type / cost center / unit ID ────────────────
    const applyUnitEntry = useCallback(
        (dept, ag, reg, un, sub, tr) => {
            UserHelperService.getUnitEntryByAllFields(dept, ag, reg, un, sub, tr)
                .then((resp) => {
                    if (resp?.length > 0) {
                        setFundingUnitType(resp[0][FieldNames.units.unitType]);
                        setCostCenterCode(resp[0][FieldNames.units.costCenterCode]);
                        setFundingUnitID(resp[0][FieldNames.units.unitID]);
                    }
                })
                .catch(logError);
        },
        [logError]
    );

    // ── Cascade-reset helpers ─────────────────────────────────────────────────
    const resetBelowAgency = () => { setRegion(''); setUnit(''); setSubUnit(''); setTribe(''); setFundingUnitType(''); };
    const resetBelowRegion = () => { setUnit(''); setSubUnit(''); setTribe(''); setFundingUnitType(''); };
    const resetBelowUnit = () => { setSubUnit(''); setTribe(''); };

    // ── Autocomplete field hooks ──────────────────────────────────────────────

    const agencyField = useAutocompleteField(async () => {
        if (!department) return [''];
        const fromRole = uniqueList(userRoleInfo ?? [], 'Agency');
        if (fromRole.length > 0) return fromRole;
        return UserHelperService.getAgencyByDepartment(department)
            .then((r) => r?.length > 0 ? r.map((x) => x.Agency) : [''])
            .catch((e) => { logError(e); return ['']; });
    }, [department]);

    const regionField = useAutocompleteField(async () => {
        if (!agency) return [''];
        const fromRole = uniqueList(userRoleInfo ?? [], 'Region');
        if (fromRole.length > 0) return fromRole;
        return UserHelperService.getRegionsByAgency(agency)
            .then((r) => r?.length > 0 ? r.map((x) => x.Region) : [''])
            .catch((e) => { logError(e); return ['']; });
    }, [agency]);

    const unitField = useAutocompleteField(async () => {
        if (!region) return [''];
        const fromRole = uniqueList(
            (userRoleInfo ?? []).filter((i) => i.Region === region), 'Unit'
        );
        if (fromRole.length > 0) return fromRole;
        if (agency) {
            return UserHelperService.getUnitsByRegionsAgency([region], agency)
                .then((r) => r?.length > 0 ? r.map((x) => x.Unit) : [''])
                .catch((e) => { logError(e); return ['']; });
        }
        return UserHelperService.getUnitsByRegions([region])
            .then((r) => r?.length > 0 ? r.map((x) => x.Unit) : [''])
            .catch((e) => { logError(e); return ['']; });
    }, [region, agency]);

    const subUnitField = useAutocompleteField(async () => {
        const fromRole = uniqueList(
            (userRoleInfo ?? []).filter((i) => i.Unit === unit), 'SubUnit'
        );
        if (fromRole.length > 0) return fromRole;
        if (!unit) return [''];
        return UserHelperService.getSubUnitsByUnits([unit])
            .then((r) => r?.length > 0 ? r.map((x) => x.SubUnit) : [''])
            .catch((e) => { logError(e); return ['']; });
    }, [unit]);

    const tribeField = useAutocompleteField(async () => {
        const fromRole = uniqueList(userRoleInfo ?? [], FieldNames.userRoles.tribe);
        if (fromRole.length > 0) return fromRole;
        if (!unit) return [''];
        return UserHelperService.getTribesByUnits([unit])
            .then((r) => r?.length > 0 ? r.map((x) => x[FieldNames.units.tribeFullName] ?? '') : [''])
            .catch((e) => { logError(e); return ['']; });
    }, [unit]);

    // ── Initialise from selectedRow ───────────────────────────────────────────
    useEffect(() => {
        if (!selectedRow) return;
        setDepartment(getRowValue(selectedRow, FieldNames.fundingFields.fundingDepartment) || FieldNames.defaults.department);
        setAgency(getRowValue(selectedRow, FieldNames.fundingFields.fundingAgency));
        setRegion(getRowValue(selectedRow, FieldNames.fundingFields.fundingRegion));
        setUnit(getRowValue(selectedRow, FieldNames.fundingFields.fundingUnit));
        setSubUnit(getRowValue(selectedRow, FieldNames.fundingFields.fundingSubUnit));
        setTribe(getRowValue(selectedRow, FieldNames.fundingFields.fundingTribe));
        setFundingUnitType(getRowValue(selectedRow, FieldNames.fundingFields.fundingUnitType));
        setCostCenterCode(getRowValue(selectedRow, FieldNames.fundingFields.costCenter));
        setFundingUnitID(getRowValue(selectedRow, FieldNames.fundingFields.fundingUnitID));
    }, [selectedRow]);

    // ── Interagency override ──────────────────────────────────────────────────
    useEffect(() => {
        if (!isInteragencyRow) return;
        setAgency(FieldNames.agencies.owf);
        setRegion('Department of the Interior OWF');
        setUnit('Office of Wildland Fire');
        setFundingUnitType(FieldNames.unitTypes.national);
    }, [isInteragencyRow]);

    // ── BIL detection ─────────────────────────────────────────────────────────
    useEffect(() => {
        const hasBil = rows?.some(
            (x) => x[FieldNames.fundingFields.fundingSource]?.toLowerCase().includes('bil')
        ) ?? false;
        setIsBil(hasBil);
    }, [rows]);

    // ── Funding unit type options ─────────────────────────────────────────────
    useEffect(() => {
        UserHelperService.getAllFundingUnitTypes()
            .then((resp) => setFundingUnitTypeOptions(resp.map((x) => x[FieldNames.units.unitType])))
            .catch(logError);
    }, [logError]);

    // ── Shared autocomplete props ─────────────────────────────────────────────
    const sharedProps = { requiredFields, isTreatment, isActual, isPoint, agencyValue: agency };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Fragment>
            {/* Department */}
            <FundingAutocomplete
                id="departmentField"
                label="Funding Department"
                value={department}
                options={departmentOptions}
                disabled
                fieldKey={FieldNames.featureFields.fundingDepartment}
                {...sharedProps}
                onChange={(_, v) => { setDepartment(v); setAgency(''); resetBelowAgency(); }}
            />

            {/* Agency */}
            <FundingAutocomplete
                id="agencyField"
                label="Funding Agency"
                value={agency}
                options={agencyField.options}
                loading={agencyField.loading}
                disabled={isInteragencyRow}
                fieldKey={FieldNames.featureFields.fundingAgency}
                {...sharedProps}
                onOpen={() => agencyField.setOpen(true)}
                onClose={() => agencyField.setOpen(false)}
                onChange={(_, v) => { setAgency(v); resetBelowAgency(); }}
            />

            {/* Region */}
            <FundingAutocomplete
                id="regionField"
                label="Funding Region"
                value={region}
                options={regionField.options}
                loading={regionField.loading}
                disabled={isInteragencyRow}
                fieldKey={FieldNames.featureFields.fundingRegion}
                {...sharedProps}
                onOpen={() => regionField.setOpen(true)}
                onClose={() => regionField.setOpen(false)}
                onChange={(_, v) => { setRegion(v); resetBelowRegion(); }}
            />

            {/* Unit */}
            <FundingAutocomplete
                id="unitField"
                label="Funding Unit"
                value={unit}
                options={unitField.options}
                loading={unitField.loading}
                disabled={isInteragencyRow}
                fieldKey={FieldNames.featureFields.fundingUnit}
                {...sharedProps}
                onOpen={() => unitField.setOpen(true)}
                onClose={() => unitField.setOpen(false)}
                onChange={(_, v) => {
                    setUnit(v); resetBelowUnit();
                    applyUnitEntry(department, agency, region, v, '', '');
                }}
            />

            {/* SubUnit — BLM only */}
            {isMatching(agency, 'blm') && (
                <FundingAutocomplete
                    id="subUnitField"
                    label="Funding SubUnit"
                    value={subUnit}
                    options={subUnitField.options}
                    loading={subUnitField.loading}
                    fieldKey={FieldNames.featureFields.fundingSubUnit}
                    {...sharedProps}
                    onOpen={() => subUnitField.setOpen(true)}
                    onClose={() => subUnitField.setOpen(false)}
                    onChange={(_, v) => {
                        setSubUnit(v);
                        applyUnitEntry(department, agency, region, unit, v, '');
                    }}
                />
            )}

            {/* Tribe — BIA only */}
            {isMatching(agency, 'bia') && (
                <FundingAutocomplete
                    id="tribeField"
                    label="Funding Tribe"
                    value={tribe}
                    options={tribeField.options}
                    loading={tribeField.loading}
                    fieldKey={FieldNames.featureFields.fundingTribe}
                    {...sharedProps}
                    onOpen={() => tribeField.setOpen(true)}
                    onClose={() => tribeField.setOpen(false)}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                            <Chip key={option} label={option[FieldNames.units.tribeFullName]} {...getTagProps({ index })} />
                        ))
                    }
                    onChange={(_, v) => {
                        setTribe(v);
                        if (v) {
                            UserHelperService.getRegionUnitWithTribe(v)
                                .then((resp) => {
                                    if (resp?.length > 0) {
                                        if (!region) setRegion(resp[0][FieldNames.units.region]);
                                        if (!unit) setUnit(resp[0][FieldNames.units.unit]);
                                    }
                                })
                                .catch(logError);
                        }
                        applyUnitEntry(department, agency, region, unit, '', v);
                    }}
                />
            )}

            {/* Funding Unit Type */}
            <FundingAutocomplete
                id="fundingUnitTypeField"
                label="Funding Unit Type"
                value={fundingUnitType}
                options={fundingUnitTypeOptions}
                disabled
                fieldKey={FieldNames.featureFields.fundingUnitType}
                {...sharedProps}
            />
        </Fragment>
    );
});

export default FundingAssignment;
